import { useCallback, useEffect, useRef, useState } from 'react';
import { initialState, reduce } from '../utils/coachMachine';
import { isTipsEnabled, subscribeTipsChanged } from '../utils/tipsPreference';
import { track } from '../utils/analytics';

// Le temps de s'installer avant le premier tooltip.
const ARM_DELAY_MS = 800;
// Duree de drone audible avant de proposer la progression.
const DRONE_TARGET_MS = 25000;
// Meme respiration apres l'arret de la progression, avant le tip Jam.
const JAM_DELAY_MS = 800;

function markOnboardingDone() {
  try {
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('firstTimePopupSeen', 'true');
  } catch {
    // stockage indisponible : l'onboarding se rejouera, sans casse
  }
}

export function isOnboardingDone() {
  try {
    return localStorage.getItem('onboardingCompleted') === 'true';
  } catch {
    return false;
  }
}

// Le guide est fini quand il est alle au bout (tap sur Jam, ou dernier tip
// affiche jusqu'a son terme) ou quand la personne coupe les tips. Toucher un
// accord ou taper Play par curiosite ne le consomme plus : c'etait le defaut
// qui faisait rater tout le parcours a ceux qui sortaient du chemin.
function endsOnboarding(type, prev) {
  if (type === 'JAM_TAPPED' || type === 'TIPS_DISABLED') return true;
  return type === 'TIP_DISMISSED' && prev === 'tapJam';
}

/**
 * Coup de pouce au premier lancement : heberge l'automate pur (coachMachine),
 * ses delais et sa persistance. App ne fait que brancher les callbacks.
 *
 * Les entrees servent a savoir si le drone est reellement audible : un chip
 * sonne, la progression est arretee, aucun panneau n'est ouvert et on est bien
 * sur l'ecran Play. Sinon le compte du drone se suspend et reprend ou il en
 * etait.
 */
export default function useGettingStarted({
  isChordSounding = false,
  isProgressionPlaying = false,
  activePanelId = null,
  currentPage = 'home'
} = {}) {
  const [coachStep, setCoachStep] = useState(initialState);
  const stepRef = useRef(initialState);

  const dispatch = useCallback((event) => {
    const prev = stepRef.current;
    const next = reduce(prev, event);
    if (next === prev) return;
    stepRef.current = next;

    const type = typeof event === 'string' ? event : event?.type;
    if (endsOnboarding(type, prev)) {
      markOnboardingDone();
    }
    if (type === 'CHORD_TAPPED') {
      track('onboarding_engaged');
    }
    if (type === 'PLAY_TAPPED') {
      track('onboarding_play_tapped', { from_tip: prev === 'tapPlay' });
    }
    if (type === 'TIP_DISMISSED') {
      track('onboarding_tip_dismissed', { step: prev, reason: event?.reason || 'timer' });
    }
    if (next === 'tapPlay') {
      track('onboarding_play_tip_shown');
    }

    setCoachStep(next);
  }, []);

  // Armement au premier lancement seulement : un rechargement ne rejoue rien.
  useEffect(() => {
    if (!isTipsEnabled() || isOnboardingDone()) return undefined;
    const timer = setTimeout(() => dispatch('ARM'), ARM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Couper les tips en cours de route termine tout de suite.
  useEffect(() => subscribeTipsChanged((enabled) => {
    if (!enabled) dispatch('TIPS_DISABLED');
  }), [dispatch]);

  // Compteur des 25 s de drone : des horodatages plutot qu'un intervalle, pour
  // que la suspension (panneau ouvert, drone coupe) reprenne au bon endroit.
  const droneElapsedRef = useRef(0);
  const droneAudible = isChordSounding && !isProgressionPlaying
    && activePanelId === null && currentPage === 'home';

  useEffect(() => {
    if (coachStep !== 'droneWait' || !droneAudible) return undefined;
    const startedAt = Date.now();
    const remaining = Math.max(0, DRONE_TARGET_MS - droneElapsedRef.current);
    const timer = setTimeout(() => dispatch('DRONE_ELAPSED'), remaining);
    return () => {
      clearTimeout(timer);
      droneElapsedRef.current += Date.now() - startedAt;
    };
  }, [coachStep, droneAudible, dispatch]);

  // Le drone coupe par l'utilisateur (front descendant du chip qui sonne, hors
  // progression) vaut "j'ai fini" : le tip Play arrive sans attendre les 25 s.
  const prevSoundingRef = useRef(isChordSounding);
  useEffect(() => {
    const wasSounding = prevSoundingRef.current;
    prevSoundingRef.current = isChordSounding;
    if (wasSounding && !isChordSounding && !isProgressionPlaying) {
      dispatch('DRONE_STOPPED');
    }
  }, [isChordSounding, isProgressionPlaying, dispatch]);

  // Le tip Jam n'arrive qu'apres une progression reellement entendue : le tap
  // sur Play declenche l'init audio, la lecture peut demarrer bien apres.
  const sawProgressionRef = useRef(false);
  useEffect(() => {
    if (isProgressionPlaying) {
      sawProgressionRef.current = true;
      return undefined;
    }
    if (coachStep !== 'waitJam' || !sawProgressionRef.current) return undefined;
    const timer = setTimeout(() => dispatch('PROGRESSION_STOPPED'), JAM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [coachStep, isProgressionPlaying, dispatch]);

  const onChordTapped = useCallback(() => dispatch('CHORD_TAPPED'), [dispatch]);
  const onPlayTapped = useCallback(() => dispatch('PLAY_TAPPED'), [dispatch]);
  const onJamTapped = useCallback(() => dispatch('JAM_TAPPED'), [dispatch]);
  const dismissTip = useCallback((reason) => dispatch({ type: 'TIP_DISMISSED', reason }), [dispatch]);

  return { coachStep, onChordTapped, onPlayTapped, onJamTapped, dismissTip };
}
